import React from "react";
import classNames from "classnames";
import { OverflowMarquee } from "@shared/ui/overflow-marquee";
import "./DetailList.scss";

/** Визуальный тон {@link DetailRow} для семантической маркировки строки. */
export type DetailRowTone = "default" | "success" | "warning" | "error" | "info" | "accent";

/** Пропсы {@link DetailRow}. */
export interface DetailRowProps {
    /** Подпись характеристики. */
    label: string;
    /** Значение; может быть строкой, числом или React-узлом. */
    value: React.ReactNode;
    /**
     * Включает ping-pong авто-прокрутку значения при overflow в `layout="tiles"`. \
     * Работает и для React-узлов, например `Business*Value`.
     * @default true
     */
    scrollable?: boolean;
    /**
     * Семантическая подсветка строки.
     * @default "default"
     */
    tone?: DetailRowTone;
    /** Дополнительный CSS-класс строки. */
    className?: string;

    /** Дополнительный обработчик клика на строку. */
    onClick?: () => void;
}

/** Визуальный размер {@link DetailCard}. */
export type DetailCardSize = "md" | "lg";

/** Тон поверхности {@link DetailCard}. */
export type DetailCardTone = "neutral" | "accent";

/** Пропсы {@link DetailCard}. */
export interface DetailCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
    /** Короткая подпись значения. */
    label: React.ReactNode;
    /** Главное read-only значение внутри поверхности. */
    value: React.ReactNode;
    /**
     * Размер карточки. \
     * `lg` делает значение заметнее для подтверждений и ключевых выбранных сущностей.
     * @default "md"
     */
    size?: DetailCardSize;
    /**
     * Визуальный тон поверхности. \
     * `accent` мягко подсвечивает карточку без использования domain-цветов.
     * @default "neutral"
     */
    tone?: DetailCardTone;
    /**
     * Включает ping-pong авто-прокрутку для строковых/числовых значений при overflow.
     * @default true
     */
    scrollable?: boolean;
    /** Дополнительный CSS-класс корневой поверхности. */
    className?: string;
    /** Дополнительный CSS-класс подписи. */
    labelClassName?: string;
    /** Дополнительный CSS-класс значения. */
    valueClassName?: string;
}

/** Режим отображения {@link DetailList}. */
export type DetailListLayout = "inline" | "stacked" | "tiles";

/** Число колонок сетки для {@link DetailListLayout} `tiles`. */
export type DetailListColumns = number | "auto";

/** Параметры автоматического выбора колонок плиточного {@link DetailList}. */
export interface GetAutoDetailListColumnsParams {
    /** Текущая ширина контейнера списка в пикселях. */
    containerWidth: number;
    /** Количество плиток, которые нужно разложить. */
    itemCount: number;
    /** Минимальная приемлемая ширина одной плитки в пикселях. */
    minWidth: number;
}

/** Пропсы {@link DetailList}. */
export interface DetailListProps {
    children: React.ReactNode;
    /** Дополнительный CSS-класс списка. */
    className?: string;
    /**
     * Расположение label/value. \
     * `inline` — одна строка; `stacked` — label над value; `tiles` — сетка плиток.
     * @default "inline"
     */
    layout?: DetailListLayout;
    /**
     * Число колонок для `layout="tiles"`. \
     * Число фиксирует сетку; `"auto"` рассчитывает колонки по ширине контейнера и {@link DetailListProps.minWidth minWidth}.
     * @default 3
     */
    columns?: DetailListColumns;
    /**
     * Минимальная приемлемая ширина плитки для `columns="auto"` в пикселях. \
     * Нужна, чтобы auto-режим не ужимал плитки до нечитаемых размеров.
     * @default 112
     */
    minWidth?: number;
    /**
     * Раскладывает `layout="tiles"` в одну горизонтальную строку с прокруткой. \
     * Для `inline` и `stacked` не влияет на отображение.
     * @default false
     */
    oneLine?: boolean;
}

type DetailListStyle = React.CSSProperties & {
    "--detail-list-columns"?: string;
    "--detail-list-min-width"?: string;
};

interface DetailListContextValue {
    layout: DetailListLayout;
}

const DetailListContext = React.createContext<DetailListContextValue>({ layout: "inline" });

function normalizeDetailListColumns(columns: number): number {
    if (!Number.isFinite(columns)) {
        return 3;
    }

    return Math.max(1, Math.floor(columns));
}

function normalizeDetailListMinWidth(minWidth: number): number {
    if (!Number.isFinite(minWidth)) {
        return 112;
    }

    return Math.max(1, Math.floor(minWidth));
}

function isScrollablePrimitiveValue(value: React.ReactNode): value is string | number {
    return typeof value === "string" || typeof value === "number";
}

/**
 * Выбирает число колонок для `layout="tiles"` по ширине контейнера. \
 * Старается не делать плитки уже `minWidth` и избегает одинокой плитки в последней строке.
 * @param params параметры расчёта колонок
 * @returns Рекомендуемое число колонок
 */
export function getAutoDetailListColumns({ containerWidth, itemCount, minWidth }: GetAutoDetailListColumnsParams): number {
    if (!Number.isFinite(containerWidth) || containerWidth <= 0 || itemCount <= 0) {
        return 1;
    }

    const normalizedItemCount = normalizeDetailListColumns(itemCount);
    const normalizedMinWidth = normalizeDetailListMinWidth(minWidth);
    const maxColumnsByWidth = Math.floor(containerWidth / normalizedMinWidth);
    const maxColumns = Math.max(1, Math.min(normalizedItemCount, maxColumnsByWidth));

    if (maxColumns > 2 && normalizedItemCount % maxColumns === 1) {
        return maxColumns - 1;
    }

    return maxColumns;
}

/**
 * Выделенная surface-карточка для одного read-only значения. \
 * Подходит для выбранной сущности, операции или другого ключевого контекста перед действием.
 *
 * @example
 * ```tsx
 * <DetailCard label="Операция к выполнению" value={operationName} size="lg" tone="accent" />
 * ```
 */
export function DetailCard({
    label,
    value,
    size = "md",
    tone = "neutral",
    scrollable = true,
    className,
    labelClassName,
    valueClassName,
    ...rootProps
}: DetailCardProps) {
    return (
        <div {...rootProps} className={classNames("detail_card", `detail_card--${size}`, `detail_card--${tone}`, className)}>
            <div className={classNames("detail_card__label", labelClassName)}>{label}</div>
            <div className={classNames("detail_card__value", valueClassName)}>
                {scrollable && isScrollablePrimitiveValue(value) ? (
                    <OverflowMarquee>{String(value)}</OverflowMarquee>
                ) : (
                    value
                )}
            </div>
        </div>
    );
}

/**
 * Одна строка label/value для read-only деталей. \
 * Расположение задаётся родительским {@link DetailList}.
 */
export function DetailRow({ label, value, scrollable = true, tone = "default", className, onClick }: DetailRowProps) {
    const { layout } = React.useContext(DetailListContext);
    const shouldRenderScrollableValue = layout === "tiles" && scrollable;

    return (
        <div
            className={classNames("detail_list__row", tone !== "default" && `detail_list__row--${tone}`, className)}
            onClick={onClick}
        >
            <dt className="detail_list__label">{label}</dt>
            <dd className="detail_list__value">
                {shouldRenderScrollableValue ? <OverflowMarquee>{value}</OverflowMarquee> : value}
            </dd>
        </div>
    );
}

/**
 * Компактный список read-only характеристик label/value. \
 * Используйте `DetailList.Row` для compound-записи или экспортированный {@link DetailRow} для совместимости.
 *
 * @example
 * ```tsx
 * <DetailList layout="tiles" columns="auto" minWidth={120}>
 *   <DetailList.Row label="Обозначение" value={sign} />
 *   <DetailList.Row label="Наименование" value={name} />
 * </DetailList>
 * ```
 */
function DetailListBase({
    children,
    className,
    layout = "inline",
    columns = 3,
    minWidth = 112,
    oneLine = false,
}: DetailListProps) {
    const listRef = React.useRef<HTMLDListElement | null>(null);
    const itemCount = React.Children.count(children);
    const [autoColumns, setAutoColumns] = React.useState(1);
    const normalizedMinWidth = normalizeDetailListMinWidth(minWidth);

    React.useLayoutEffect(() => {
        if (layout !== "tiles" || columns !== "auto") {
            return;
        }

        const list = listRef.current;
        if (list == null) {
            return;
        }

        const updateColumns = (): void => {
            const { width } = list.getBoundingClientRect();
            const nextColumns = getAutoDetailListColumns({
                containerWidth: width,
                itemCount,
                minWidth: normalizedMinWidth,
            });

            setAutoColumns((currentColumns) => (currentColumns === nextColumns ? currentColumns : nextColumns));
        };

        updateColumns();

        const observer = new ResizeObserver(updateColumns);
        observer.observe(list);

        return () => {
            observer.disconnect();
        };
    }, [columns, itemCount, layout, normalizedMinWidth]);

    const resolvedColumns = columns === "auto" ? autoColumns : normalizeDetailListColumns(columns);
    const listStyle: DetailListStyle | undefined =
        layout === "tiles"
            ? {
                  "--detail-list-columns": String(resolvedColumns),
                  "--detail-list-min-width": `${normalizedMinWidth}px`,
              }
            : undefined;

    return (
        <DetailListContext.Provider value={{ layout }}>
            <dl
                ref={listRef}
                className={classNames(
                    "detail_list",
                    `detail_list--${layout}`,
                    layout === "tiles" && oneLine && "detail_list--one_line",
                    className,
                )}
                style={listStyle}
            >
                {children}
            </dl>
        </DetailListContext.Provider>
    );
}

/** Compound-компонент списка деталей с `Row` и `Card` для удобной композиции. */
export const DetailList = Object.assign(DetailListBase, {
    Row: DetailRow,
    Card: DetailCard,
});
